import React, { useState, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { GlassCard, GlassCardHeader, GlassCardTitle, GlassCardDescription, GlassCardContent } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, RefreshCw, FilePlus, Trash } from "lucide-react";
import { PatchPolicyFormValues } from "@/components/patching/schema";
import { ConfigurationWithId, AbmConfig } from "@/types/settings";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import CustomerTag from "@/components/CustomerTag";
import { mockCustomers } from "@/utils/customerGroups";
import { AbmSettingsForm } from "@/components/patching/sections/settings/AbmSettingsForm";
import { ApnsConfigurationList } from "@/components/patching/sections/settings/apns/ApnsConfigurationList";
import { toast } from "sonner";

const STORAGE_KEYS = {
  ABM_CONFIGS: 'patching_abm_configs'
};

interface AppleSettingsProps {
  form: UseFormReturn<PatchPolicyFormValues>;
}

export function AppleSettings({ form }: AppleSettingsProps) {
  const navigate = useNavigate();
  
  // State for navigation confirmation dialog
  const [createProfileDialogOpen, setCreateProfileDialogOpen] = useState(false);
  const [selectedAbmConfigForProfile, setSelectedAbmConfigForProfile] = useState<AbmConfig | null>(null);

  // ABM configs state
  const [abmConfigs, setAbmConfigs] = useState<AbmConfig[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ABM_CONFIGS);
      return stored ? JSON.parse(stored) : [{
        id: "default",
        name: "Corporate ABM Account",
        customerId: "c3",
        isDefault: true,
        organizationId: "org-12345",
        serverToken: "token-abcxyz"
      }];
    } catch (e) {
      console.error("Error loading ABM configs from localStorage:", e);
      return [{
        id: "default",
        name: "Corporate ABM Account",
        customerId: "c3",
        isDefault: true,
        organizationId: "org-12345",
        serverToken: "token-abcxyz"
      }];
    }
  });

  // Dialog state
  const [openAbmDialog, setOpenAbmDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ConfigurationWithId | null>(null);

  // Persist ABM configs
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ABM_CONFIGS, JSON.stringify(abmConfigs));
  }, [abmConfigs]);

  // ABM handlers
  const addAbmConfig = (config: AbmConfig) => {
    if (editingConfig) {
      setAbmConfigs(abmConfigs.map(c => c.id === editingConfig.id ? config : c));
      toast.success("ABM configuration updated successfully");
    } else {
      const newId = `abm-${Date.now()}`;
      setAbmConfigs([...abmConfigs, {
        ...config,
        id: newId,
        isDefault: false
      }]);
      toast.success("ABM configuration added successfully");
    }
    setOpenAbmDialog(false);
    setEditingConfig(null);
  };

  const removeAbmConfig = (id: string) => {
    setAbmConfigs(abmConfigs.filter(config => config.id !== id));
    toast.success("ABM configuration removed");
  };

  const editAbmConfig = (config: AbmConfig) => {
    setEditingConfig(config);
    setOpenAbmDialog(true);
  };

  const syncAbmConfig = (config: AbmConfig) => {
    toast.info(`Syncing ${config.name}...`);
    setTimeout(() => {
      toast.success(`${config.name} synced successfully`);
    }, 1500);
  };

  const handleCreateEnrollmentProfile = (config: AbmConfig) => {
    setSelectedAbmConfigForProfile(config);
    setCreateProfileDialogOpen(true);
  };

  const confirmNavigateToEnrollmentProfiles = () => {
    setCreateProfileDialogOpen(false);
    navigate("/admin/enrollment-profiles");
  };

  const getCustomerName = (id: string) => {
    return mockCustomers.find(c => c.id === id)?.name || "Global";
  };

  return (
    <GlassCard variant="default" hover={false}>
      <GlassCardHeader>
        <GlassCardTitle>Apple Configuration</GlassCardTitle>
        <GlassCardDescription>
          Configure Apple Push Notification certificates and Apple Business Manager integration
        </GlassCardDescription>
      </GlassCardHeader>
      <GlassCardContent className="space-y-8">
        {/* APNS Settings Section */}
        <section className="space-y-4">
          <ApnsConfigurationList />
        </section>

        {/* ABM Settings Section */}
        <section className="space-y-4 pt-6 border-t">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Apple Business Manager Configuration</h3>
            <Button onClick={() => {
              setEditingConfig(null);
              setOpenAbmDialog(true);
            }}>
              <Plus className="w-4 h-4 mr-2" />
              Add ABM Configuration
            </Button>
          </div>
        
          <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {abmConfigs.map(config => (
              <GlassCard key={config.id} variant="subtle" hover={true} className="overflow-hidden">
                <div className="p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium text-lg">{config.name}</h3>
                    <ActionsDropdown
                      actions={[
                        {
                          label: "Edit",
                          icon: Pencil,
                          onClick: () => editAbmConfig(config),
                        },
                        {
                          label: "Sync",
                          icon: RefreshCw,
                          onClick: () => syncAbmConfig(config),
                        },
                        {
                          label: "Create Enrollment Profile",
                          icon: FilePlus,
                          onClick: () => handleCreateEnrollmentProfile(config),
                        },
                        ...(config.isDefault ? [] : [{
                          label: "Delete",
                          icon: Trash,
                          onClick: () => removeAbmConfig(config.id),
                          destructive: true,
                        }]),
                      ]}
                    />
                  </div>
                  <div className="text-sm text-muted-foreground line-clamp-2">
                    {config.organizationId && <span className="block">Org ID: {config.organizationId}</span>}
                  </div>
                  <div className="pt-2">
                    <CustomerTag customer={getCustomerName(config.customerId)} />
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        </section>

        {/* ABM Edit Dialog */}
        <Dialog open={openAbmDialog} onOpenChange={(open) => {
          setOpenAbmDialog(open);
          if (!open) setEditingConfig(null);
        }}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>{editingConfig ? "Edit ABM Configuration" : "Add ABM Configuration"}</DialogTitle>
            </DialogHeader>
            <AbmSettingsForm 
              initialData={editingConfig as AbmConfig} 
              onSubmit={addAbmConfig} 
              onCancel={() => setOpenAbmDialog(false)} 
              form={form} 
            />
          </DialogContent>
        </Dialog>

        {/* Create Enrollment Profile Confirmation Dialog */}
        <AlertDialog open={createProfileDialogOpen} onOpenChange={setCreateProfileDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Create Enrollment Profile</AlertDialogTitle>
              <AlertDialogDescription>
                You will be navigated to the Enrollment Profiles section where you can create a new enrollment profile
                {selectedAbmConfigForProfile && (
                  <> using the <strong>{selectedAbmConfigForProfile.name}</strong> ABM configuration.</>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmNavigateToEnrollmentProfiles}>
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </GlassCardContent>
    </GlassCard>
  );
}
